import { type Vector3Like } from 'three'

enum EBodyPart {
  HEAD = 'head',
  TORSO = 'torso',
  ARMS = 'arms',
  HANDS = 'hands',
  LOWER_BODY = 'lower body',
  LEGS = 'legs',
  FEET = 'feet',
  LIPS = 'lips',
  GROIN = 'groin',
}

enum EBodyZone {
  FRONT = 'front',
  BACK = 'back',
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom',
  FRONT_LEFT = 'front-left',
  FRONT_RIGHT = 'front-right',
  BACK_LEFT = 'back-left',
  BACK_RIGHT = 'back-right',
  TOP_LEFT = 'top-left',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_RIGHT = 'bottom-right',
  SCALP = 'scalp',
  BUTTOCKS = 'buttocks',
  UNDERARMS = 'underarms',
}

enum EBodySubCategory {
  SIDE_VIEW = 'side view',
  UNDERARMS = 'underarms',
  BACK_OF_ARMS = 'back of arms',
  FEET = 'feet',
}

type BodyPartBase = {
  id: string
  name: EBodyPart
  icon: string
}

type BodyPartWithChildren = BodyPartBase & {
  children: StaticPosition[]
}

type BodyPartIndependent = BodyPartBase & {
  position: Vector3Like
  target: Vector3Like
}

export type BodyParts = BodyPartWithChildren | BodyPartIndependent

export type StaticPosition = {
  id: string
  name: EBodyZone
  label?: string
  subCategory?: EBodySubCategory
  position: Vector3Like
  target: Vector3Like
}

export type StaticPositionsVersionKey = 'v1' | 'v2'

const STATIC_POSITIONS_V1: BodyParts[] = [
  {
    id: 'head',
    name: EBodyPart.HEAD,
    icon: 'healthicons:head-outline',
    children: [
      {
        id: 'front-head',
        name: EBodyZone.FRONT,
        position: { x: 0.3510086203464686, y: 44.86891839345276, z: 22.64939590113562 },
        target: { x: -0.3788334723380865, y: 41.15888488936371, z: -5.529503427324806 },
      },
      {
        id: 'back-head',
        name: EBodyZone.BACK,
        position: { x: -0.09222665762662954, y: 40.750058341679356, z: -23.208010490322124 },
        target: { x: 0.20605235344564157, y: 42.45499057893623, z: -5.556646041156312 },
      },
      {
        id: 'left-head',
        name: EBodyZone.LEFT,
        position: { x: 23.571763616900313, y: 42.144158473671354, z: 1.8719843449214166 },
        target: { x: -0.41774560571186103, y: 41.769506182937015, z: -0.08326044618650774 },
      },
      {
        id: 'right-head',
        name: EBodyZone.RIGHT,
        position: { x: -24.7239953823619, y: 42.3057777878498, z: 1.0334010896083012 },
        target: { x: 0.365014710277816, y: 42.515431601720586, z: -0.6892792670799172 },
      },
      {
        id: 'scalp',
        name: EBodyZone.SCALP,
        position: { x: 0.0521284512743372, y: 70.88336327608562, z: 2.6261727604955465 },
        target: { x: 0.0521284512743372, y: 42.97253547707407, z: 0.39330653657452647 },
      },
    ],
  },
  {
    id: 'torso',
    name: EBodyPart.TORSO,
    icon: 'icon-park:chest',
    children: [
      {
        id: 'front-torso',
        name: EBodyZone.FRONT,
        position: { x: 2.5062163565388724, y: 20.03124206079098, z: 46.57946698152826 },
        target: { x: 0.04094524714705728, y: 18.872094923903706, z: -5.16116330510277 },
      },

      {
        id: 'back-torso',
        name: EBodyZone.BACK,
        position: { x: -0.08, y: 18.10504537348555, z: -50.39742289142582 },
        target: { x: -0.08, y: 15.35, z: -4.48 },
      },
      {
        id: 'navel',
        name: EBodyZone.FRONT,
        label: 'navel',
        position: { x: 0, y: 12.5, z: 28 },
        target: { x: 0, y: 10.5, z: 0 },
      },
    ],
  },
  {
    id: 'arms',
    name: EBodyPart.ARMS,
    icon: 'healthicons:arm-outline',
    children: [
      {
        id: 'left-arm',
        name: EBodyZone.LEFT,
        label: 'left arm',
        subCategory: EBodySubCategory.SIDE_VIEW,
        position: { x: 28.4158085306059, y: 14.064446307407808, z: 56.96868727155726 },
        target: { x: 25.404587564524373, y: 12.64859871641834, z: -6.230232361997011 },
      },
      {
        id: 'right-arm',
        name: EBodyZone.RIGHT,
        label: 'right arm',
        subCategory: EBodySubCategory.SIDE_VIEW,
        position: { x: -22.329465372427382, y: 15.951346521474223, z: 59.34425955373744 },
        target: { x: -25.34068498219712, y: 14.535498872279051, z: -3.854660143136831 },
      },
      {
        id: 'left-underarm',
        name: EBodyZone.LEFT,
        label: 'left underarm',
        subCategory: EBodySubCategory.UNDERARMS,
        position: { x: 29.13, y: 13.41, z: 34.71 },
        target: { x: 17.13, y: 20.41, z: -3.29 },
      },
      {
        id: 'right-underarm',
        name: EBodyZone.RIGHT,
        label: 'right underarm',
        subCategory: EBodySubCategory.UNDERARMS,
        position: { x: -29.08, y: 13.46, z: 34.76 },
        target: { x: -17.08, y: 20.46, z: -3.24 },
      },
      {
        id: 'back-left-arm',
        name: EBodyZone.LEFT,
        label: 'back left arm',
        subCategory: EBodySubCategory.BACK_OF_ARMS,
        position: { x: 28.98, y: 18.67, z: -46.1 },
        target: { x: 22.98, y: 15.67, z: -4.1 },
      },
      {
        id: 'back-right-arm',
        name: EBodyZone.RIGHT,
        label: 'back right arm',
        subCategory: EBodySubCategory.BACK_OF_ARMS,
        position: { x: -28.69, y: 18.97, z: -46.13 },
        target: { x: -22.69, y: 15.97, z: -4.13 },
      },
    ],
  },
  {
    id: 'hands',
    name: EBodyPart.HANDS,
    icon: 'mdi:hand-back-right-outline',
    children: [
      {
        id: 'front-left-hand',
        name: EBodyZone.FRONT_LEFT,
        position: { x: 53.34957608713596, y: 7.630328404754197, z: 12.323293546035929 },
        target: { x: 32.21207739294774, y: 3.9760381169669174, z: 1.5598223844740151 },
      },
      {
        id: 'front-right-hand',
        name: EBodyZone.FRONT_RIGHT,
        position: { x: -54.23279605352909, y: 7.441189941266948, z: 10.448423152006045 },
        target: { x: -32.33602170718, y: 4.000930575347724, z: 1.2453862228034414 },
      },
      {
        id: 'back-left-hand',
        name: EBodyZone.BACK_LEFT,
        position: { x: 17.717776339870163, y: -9.222481568286348, z: -11.652135157022887 },
        target: { x: 33.12190110773411, y: 3.816483673743418, z: 1.3362567970729438 },
      },
      {
        id: 'back-right-hand',
        name: EBodyZone.BACK_RIGHT,
        position: { x: -16.125190497172547, y: -9.549947496277515, z: -10.300596577061599 },
        target: { x: -32.42159889955349, y: 3.891000909354091, z: 1.090978342147568 },
      },
    ],
  },
  {
    id: 'lower_body',
    name: EBodyPart.LOWER_BODY,
    icon: 'healthicons:joints-outline',
    children: [
      {
        id: 'front-lower-body',
        name: EBodyZone.FRONT,
        position: { x: 2.8731231885448554, y: -6.838556949789995, z: 47.92614998133329 },
        target: { x: -0.6563280110264708, y: -7.911170597082219, z: -4.560129149279048 },
      },
      {
        id: 'buttocks',
        name: EBodyZone.BUTTOCKS,
        position: { x: -0.069999999999994, y: -0.1537812605666331, z: -53.45773424291703 },
        target: { x: -0.07, y: -4.079999999999998, z: -4.38 },
      },
    ],
  },
  {
    id: 'legs',
    name: EBodyPart.LEGS,
    icon: 'healthicons:leg-outline',
    children: [
      {
        id: 'front-legs',
        name: EBodyZone.FRONT,
        position: { x: 6.5840830634292535, y: -15.909172009390849, z: 63.98613550978011 },
        target: { x: 0.11578485130529145, y: -23.517587477951757, z: 3.4356931962604786 },
      },
      {
        id: 'back-legs',
        name: EBodyZone.BACK,
        position: { x: -0.06999999999999099, y: -23.60202333155356, z: -76.28470835558046 },
        target: { x: -0.07, y: -29.46, z: -3.06 },
      },
      {
        id: 'front-left-foot',
        name: EBodyZone.FRONT_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 4.974930207343953, y: -16.498653372523137, z: 56.13270722274315 },
        target: { x: 4.974930698868418, y: -23.62292753892011, z: 3.178131406635755 },
      },
      {
        id: 'front-right-foot',
        name: EBodyZone.FRONT_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -7.3696987884857075, y: -15.391624027812522, z: 55.983772281652506 },
        target: { x: -7.369698296961242, y: -22.515898194209495, z: 3.0291964655451147 },
      },
      {
        id: 'back-left-foot',
        name: EBodyZone.BACK_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 0.2775516295186673, y: -27.605349140546753, z: -55.575365940706384 },
        target: { x: 7.491794329832694, y: -24.53688971881274, z: 4.3452707121968555 },
      },
      {
        id: 'back-right-foot',
        name: EBodyZone.BACK_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -6.846295921600856, y: -28.206838812828412, z: -54.50313129412209 },
        target: { x: -4.301250313126381, y: -25.39459298411282, z: 5.809035422891029 },
      },
      {
        id: 'top-left-foot',
        name: EBodyZone.TOP_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 13.390444234691536, y: -19.228504455119257, z: 6.773258059351548 },
        target: { x: 12.002126370410997, y: -46.99486174072992, z: 3.441295185078248 },
      },
      {
        id: 'top-right-foot',
        name: EBodyZone.TOP_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -13.972264364658765, y: -19.14355698056685, z: 6.010437383928287 },
        target: { x: -12.583946500378222, y: -46.90991426617751, z: 2.678474509654989 },
      },
      {
        id: 'bottom-left-foot',
        name: EBodyZone.BOTTOM_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 12.118697367561248, y: -77.70488585514182, z: 1.5423163384035286 },
        target: { x: 12.11870700183076, y: -46.996602323594296, z: 1.5423454971292136 },
      },
      {
        id: 'bottom-right-foot',
        name: EBodyZone.BOTTOM_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -12.118697367561248, y: -77.70488585514182, z: 1.5423163384035286 },
        target: { x: -12.11870700183076, y: -46.996602323594296, z: 1.5423454971292136 },
      },
    ],
  },
  {
    id: 'groin',
    name: EBodyPart.GROIN,
    icon: 'healthicons:vagina-alt-outline',
    position: { x: 0.017143804815149877, y: 2.826030783601481, z: 31.08219559084092 },
    target: { x: 0.017144091657317048, y: -1.331528778951082, z: 0.17914526978394135 },
  },
  {
    id: 'mouth',
    name: EBodyPart.LIPS,
    icon: 'healthicons:mouth-outline',
    position: { x: 0.21056118467185198, y: 38.73381292648781, z: 11.242921976663201 },
    target: { x: -0.2659921445411064, y: 37.91269251847371, z: -5.068038168602485 },
  },
]

const STATIC_POSITIONS_V2: BodyParts[] = [
  {
    id: 'head',
    name: EBodyPart.HEAD,
    icon: 'healthicons:head-outline',
    children: [
      {
        id: 'front-head',
        name: EBodyZone.FRONT,
        position: { x: 0.3510086203464686, y: 44.86891839345276, z: 22.64939590113562 },
        target: { x: -0.3788334723380865, y: 41.15888488936371, z: -5.529503427324806 },
      },
      {
        id: 'back-head',
        name: EBodyZone.BACK,
        position: { x: -0.09222665762662954, y: 40.750058341679356, z: -23.208010490322124 },
        target: { x: 0.20605235344564157, y: 42.45499057893623, z: -5.556646041156312 },
      },
      {
        id: 'left-head',
        name: EBodyZone.LEFT,
        position: { x: 23.571763616900313, y: 42.144158473671354, z: 1.8719843449214166 },
        target: { x: -0.41774560571186103, y: 41.769506182937015, z: -0.08326044618650774 },
      },
      {
        id: 'right-head',
        name: EBodyZone.RIGHT,
        position: { x: -24.7239953823619, y: 42.3057777878498, z: 1.0334010896083012 },
        target: { x: 0.365014710277816, y: 42.515431601720586, z: -0.6892792670799172 },
      },
      {
        id: 'scalp',
        name: EBodyZone.SCALP,
        position: { x: 0.0521284512743372, y: 70.88336327608562, z: 2.6261727604955465 },
        target: { x: 0.0521284512743372, y: 42.97253547707407, z: 0.39330653657452647 },
      },
    ],
  },
  {
    id: 'torso',
    name: EBodyPart.TORSO,
    icon: 'icon-park:chest',
    children: [
      {
        id: 'front-torso',
        name: EBodyZone.FRONT,
        position: { x: 2.5062163565388724, y: 20.03124206079098, z: 46.57946698152826 },
        target: { x: 0.04094524714705728, y: 18.872094923903706, z: -5.16116330510277 },
      },

      {
        id: 'back-torso',
        name: EBodyZone.BACK,
        position: { x: -0.08, y: 18.10504537348555, z: -50.39742289142582 },
        target: { x: -0.08, y: 15.35, z: -4.48 },
      },
      {
        id: 'navel',
        name: EBodyZone.FRONT,
        label: 'navel',
        position: { x: 0, y: 12.5, z: 28 },
        target: { x: 0, y: 10.5, z: 0 },
      },
    ],
  },
  {
    id: 'arms',
    name: EBodyPart.ARMS,
    icon: 'healthicons:arm-outline',
    children: [
      {
        id: 'left-arm',
        name: EBodyZone.LEFT,
        label: 'left arm',
        subCategory: EBodySubCategory.SIDE_VIEW,
        position: { x: 28.4158085306059, y: 14.064446307407808, z: 56.96868727155726 },
        target: { x: 25.404587564524373, y: 12.64859871641834, z: -6.230232361997011 },
      },
      {
        id: 'right-arm',
        name: EBodyZone.RIGHT,
        label: 'right arm',
        subCategory: EBodySubCategory.SIDE_VIEW,
        position: { x: -25.329465372427382, y: 15.951346521474223, z: 59.34425955373744 },
        target: { x: -28.34068498219712, y: 14.535498872279051, z: -3.854660143136831 },
      },
      {
        id: 'left-underarm',
        name: EBodyZone.LEFT,
        label: 'left underarm',
        subCategory: EBodySubCategory.UNDERARMS,
        position: { x: 29.13, y: 13.41, z: 34.71 },
        target: { x: 17.13, y: 20.41, z: -3.29 },
      },
      {
        id: 'right-underarm',
        name: EBodyZone.RIGHT,
        label: 'right underarm',
        subCategory: EBodySubCategory.UNDERARMS,
        position: { x: -29.08, y: 13.46, z: 34.76 },
        target: { x: -17.08, y: 20.46, z: -3.24 },
      },
      {
        id: 'back-left-arm',
        name: EBodyZone.LEFT,
        label: 'back left arm',
        subCategory: EBodySubCategory.BACK_OF_ARMS,
        position: { x: 28.98, y: 18.67, z: -46.1 },
        target: { x: 22.98, y: 15.67, z: -4.1 },
      },
      {
        id: 'back-right-arm',
        name: EBodyZone.RIGHT,
        label: 'back right arm',
        subCategory: EBodySubCategory.BACK_OF_ARMS,
        position: { x: -28.69, y: 18.97, z: -46.13 },
        target: { x: -22.69, y: 15.97, z: -4.13 },
      },
    ],
  },
  {
    id: 'hands',
    name: EBodyPart.HANDS,
    icon: 'mdi:hand-back-right-outline',
    children: [
      {
        id: 'front-left-hand',
        name: EBodyZone.FRONT_LEFT,
        position: { x: 53.34957608713596, y: 7.630328404754197, z: 12.323293546035929 },
        target: { x: 32.21207739294774, y: 3.9760381169669174, z: 1.5598223844740151 },
      },
      {
        id: 'front-right-hand',
        name: EBodyZone.FRONT_RIGHT,
        position: { x: -54.23279605352909, y: 7.441189941266948, z: 10.448423152006045 },
        target: { x: -32.33602170718, y: 4.000930575347724, z: 1.2453862228034414 },
      },
      {
        id: 'back-left-hand',
        name: EBodyZone.BACK_LEFT,
        position: { x: 17.717776339870163, y: -9.222481568286348, z: -11.652135157022887 },
        target: { x: 33.12190110773411, y: 3.816483673743418, z: 1.3362567970729438 },
      },
      {
        id: 'back-right-hand',
        name: EBodyZone.BACK_RIGHT,
        position: { x: -16.125190497172547, y: -9.549947496277515, z: -10.300596577061599 },
        target: { x: -32.42159889955349, y: 3.891000909354091, z: 1.090978342147568 },
      },
    ],
  },
  {
    id: 'lower_body',
    name: EBodyPart.LOWER_BODY,
    icon: 'healthicons:joints-outline',
    children: [
      {
        id: 'front-lower-body',
        name: EBodyZone.FRONT,
        position: { x: 2.8731231885448554, y: -6.838556949789995, z: 47.92614998133329 },
        target: { x: -0.6563280110264708, y: -7.911170597082219, z: -4.560129149279048 },
      },
      {
        id: 'buttocks',
        name: EBodyZone.BUTTOCKS,
        position: { x: -0.069999999999994, y: -0.1537812605666331, z: -53.45773424291703 },
        target: { x: -0.07, y: -4.079999999999998, z: -4.38 },
      },
    ],
  },
  {
    id: 'legs',
    name: EBodyPart.LEGS,
    icon: 'healthicons:leg-outline',
    children: [
      {
        id: 'front-legs',
        name: EBodyZone.FRONT,
        position: { x: 6.5840830634292535, y: -15.909172009390849, z: 63.98613550978011 },
        target: { x: 0.11578485130529145, y: -23.517587477951757, z: 3.4356931962604786 },
      },
      {
        id: 'back-legs',
        name: EBodyZone.BACK,
        position: { x: -0.06999999999999099, y: -23.60202333155356, z: -76.28470835558046 },
        target: { x: -0.07, y: -29.46, z: -3.06 },
      },
      {
        id: 'front-left-foot',
        name: EBodyZone.FRONT_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 4.974930207343953, y: -16.498653372523137, z: 56.13270722274315 },
        target: { x: 4.974930698868418, y: -23.62292753892011, z: 3.178131406635755 },
      },
      {
        id: 'front-right-foot',
        name: EBodyZone.FRONT_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -7.3696987884857075, y: -15.391624027812522, z: 55.983772281652506 },
        target: { x: -7.369698296961242, y: -22.515898194209495, z: 3.0291964655451147 },
      },
      {
        id: 'back-left-foot',
        name: EBodyZone.BACK_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 0.2775516295186673, y: -27.605349140546753, z: -55.575365940706384 },
        target: { x: 7.491794329832694, y: -24.53688971881274, z: 4.3452707121968555 },
      },
      {
        id: 'back-right-foot',
        name: EBodyZone.BACK_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -6.846295921600856, y: -28.206838812828412, z: -54.50313129412209 },
        target: { x: -4.301250313126381, y: -25.39459298411282, z: 5.809035422891029 },
      },
      {
        id: 'top-left-foot',
        name: EBodyZone.TOP_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 13.390444234691536, y: -19.228504455119257, z: 6.773258059351548 },
        target: { x: 12.002126370410997, y: -46.99486174072992, z: 3.441295185078248 },
      },
      {
        id: 'top-right-foot',
        name: EBodyZone.TOP_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -13.972264364658765, y: -19.14355698056685, z: 6.010437383928287 },
        target: { x: -12.583946500378222, y: -46.90991426617751, z: 2.678474509654989 },
      },
      {
        id: 'bottom-left-foot',
        name: EBodyZone.BOTTOM_LEFT,
        subCategory: EBodySubCategory.FEET,
        position: { x: 12.118697367561248, y: -77.70488585514182, z: 1.5423163384035286 },
        target: { x: 12.11870700183076, y: -46.996602323594296, z: 1.5423454971292136 },
      },
      {
        id: 'bottom-right-foot',
        name: EBodyZone.BOTTOM_RIGHT,
        subCategory: EBodySubCategory.FEET,
        position: { x: -12.118697367561248, y: -77.70488585514182, z: 1.5423163384035286 },
        target: { x: -12.11870700183076, y: -46.996602323594296, z: 1.5423454971292136 },
      },
    ],
  },
  {
    id: 'groin',
    name: EBodyPart.GROIN,
    icon: 'healthicons:vagina-alt-outline',
    position: { x: 0.017143804815149877, y: 2.826030783601481, z: 31.08219559084092 },
    target: { x: 0.017144091657317048, y: -1.331528778951082, z: 0.17914526978394135 },
  },
  {
    id: 'mouth',
    name: EBodyPart.LIPS,
    icon: 'healthicons:mouth-outline',
    position: { x: 0.21056118467185198, y: 38.73381292648781, z: 11.242921976663201 },
    target: { x: -0.2659921445411064, y: 37.91269251847371, z: -5.068038168602485 },
  },
]

export const STATIC_POSITIONS: Record<StaticPositionsVersionKey, BodyParts[]> = {
  v1: STATIC_POSITIONS_V1,
  v2: STATIC_POSITIONS_V2,
}
